import { useContext, useEffect, useState } from "react";
import { ModalContext } from "../../../utils/modal/ModalProvider";
import styles from "./GroupDetailModal.module.css";
import { IAM_SERVER_SERVICE_GROUP } from "../../../utils/Config";
import axios from "../../../utils/api/axios";
import { containsXss } from "../../../utils/security/XssFilter";
import { InnocodeButton, InnocodeInput, useDialog, useInnocodeLoadingOverlay } from "@intsoft/innocode-core";



/**
 * 사용자 상세정보 모달 Container
 * @param id
 * @param update
 * @returns {JSX.Element}
 * @constructor
 */
export const GroupDetailModal = ({ id, update }) => {
    const { closeModal } = useContext(ModalContext);
    const { showDialog } = useDialog();
    const { show: showLoading, hide: hideLoading } = useInnocodeLoadingOverlay();

    const [formData, setFormData] = useState({
        linkServiceGroupId: '',
        linkServiceId: '',
        groupName: '',
        groupDescription: '',
        parentGroupId: '',
        parentGroupName: ''
    });

    useEffect(() => {
        const fetchPageData = async () => {
            showLoading();
            try {
                // 1. 그룹 정보 조회
                const url = `${IAM_SERVER_SERVICE_GROUP}/get/${id}`;
                const res = await axios.get(url);
                const data = res.data.data;

                let parentGroupName = '';
                // 2. 그룹 페이지 있으면 추가 조회
                if (data.parentGroupId !== null && data.parentGroupId !== '') {
                    try {
                        const parentUrl = `${IAM_SERVER_SERVICE_GROUP}/get/${data.parentGroupId}`;
                        const parentRes = await axios.get(parentUrl);
                        parentGroupName = parentRes.data.data.groupName || '';
                    } catch (parentErr) {
                        // 부모 페이지 정보가 없어도 에러만 콘솔에 기록하고 진행
                        console.warn('부모 페이지 조회 실패:', parentErr);
                    }
                }

                console.log("data", data);

                // 3. 폼 데이터 세팅
                setFormData({
                    linkServiceGroupId: data.serviceGroupId || '',
                    linkServiceId: data.serviceId || '',
                    groupName: data.groupName || '',
                    groupDescription: data.groupDescription || '',
                    parentGroupId: data.parentGroupId || '',
                    parentGroupName: parentGroupName
                });
            } catch (e) {
                closeModal();
                showDialog({
                    iconType: "warning",
                    type: 'error',
                    title: "실패",
                    message: "그룹 정보 조회에 실패했습니다.",
                    confirmText: "확인",
                });
            } finally {
                hideLoading();
            }
        };

        fetchPageData();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }));
    }

    // 그룹 수정 요청 API
    const handleUpdate = () => {
        console.log("formData", formData);
        for (let key in formData) {

            const value = formData[key];
            if (key !== 'parentGroupId' && value === '' && key !== 'groupDescription') {
                showDialog({
                    iconType: "warning",
                    type: 'error',
                    title: "경고",
                    message: "빈 칸을 확인해 주세요.",
                    confirmText: "확인",
                });
                return;
            } else if (key !== 'parentGroupId' && typeof value === 'string' && containsXss(value)) {
                setFormData((prevState) => ({
                    ...prevState,
                    [key]: ''
                }));
                showDialog({
                    iconType: "warning",
                    type: 'error',
                    title: "경고",
                    message: "올바른 값을 입력해 주세요.",
                    confirmText: "확인",
                });
                return;
            }
        }

        const pageParams = {
            serviceGroupId: formData.linkServiceGroupId,
            serviceId: formData.linkServiceId,
            groupName: formData.groupName,
            groupDescription: formData.groupDescription,
            parentGroupId: formData.parentGroupId
        };

        showDialog({
            iconType: "info",
            type: 'confirm',
            title: "수정 확인",
            message: "그룹 정보를 수정하시겠습니까?",
            confirmText: "예",
            cancelText: "아니요",
            onConfirm: () => {
                const url = IAM_SERVER_SERVICE_GROUP + "/update";
                axios.post(url, pageParams)
                    .then((res) => {
                        showDialog({
                            iconType: "check",
                            type: 'success',
                            title: "성공",
                            message: res.data.message,
                            confirmText: "확인",
                            onConfirm: () => {
                                update();
                                closeModal();
                            }
                        });
                    })
                    .catch((e) => {
                        showDialog({
                            iconType: "warning",
                            type: 'error',
                            title: "실패",
                            message: e.response.data.resultMessage,
                            confirmText: "확인",
                        });
                    });
            }
        });
    }

    const handleDelete = () => {
        showDialog({
            iconType: "warning",
            type: 'delete',
            title: "삭제 확인",
            message: "그룹을 삭제하시겠습니까?",
            confirmText: "예",
            cancelText: "아니요",
            onConfirm: async () => {
                const url = IAM_SERVER_SERVICE_GROUP + "/delete/" + formData.linkServiceGroupId;

                await axios.post(url)
                    .then((res) => {
                        showDialog({
                            iconType: "check",
                            type: 'info',
                            title: "성공",
                            message: res.data.message,
                            confirmText: "확인",
                            onConfirm: () => {
                                update();
                                closeModal();
                            }
                        });
                    })
                    .catch((e) => {
                        showDialog({
                            iconType: "warning",
                            type: 'error',
                            title: "실패",
                            message: e.response.data.resultMessage,
                            confirmText: "확인",
                        });
                    });
            }
        });
    }

    return (
        <div style={{ overflow: "auto" }}>
            <ul className={styles.layer__listWrap}>
                <li className={styles.layer__list}>
                    <p className={styles.layer__listTit}>그룹 명</p>
                    <InnocodeInput
                        name="groupName"
                        value={formData.groupName}
                        onChange={(e) => handleChange(e)}
                        placeholder="그룹 명"
                    />
                </li>
                <li className={styles.layer__list}>
                    <p className={styles.layer__listTit}>설명</p>
                    <InnocodeInput
                        name="groupDescription"
                        value={formData.groupDescription}
                        onChange={(e) => handleChange(e)}
                        placeholder="설명"
                        multiline={true}
                        rows={4}
                    />
                </li>
                <li className={styles.layer__list}>
                    <p className={styles.layer__listTit}>상위 그룹</p>
                    <InnocodeInput
                        name="parentGroupName"
                        value={formData.parentGroupName}
                        readonly={true}
                        placeholder="상위 그룹"
                    />
                </li>
            </ul>

            <div className={styles.layer__sensorBtnBox}>
                <InnocodeButton
                    size="lg"
                    backgroundColor="var(--status-negative, #f83a3a)"
                    onClick={handleDelete}
                >
                    삭제
                </InnocodeButton>
                <InnocodeButton
                    size="lg"
                    onClick={handleUpdate}
                >
                    수정
                </InnocodeButton>
            </div>
        </div>
    );
}